import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Point } from '@os/engine';
import { Menu, pointAnchor } from '../../ui';
import { useDeferredClose } from '../../ui/deferred-close';

export type QuickKind = 'sticky' | 'shape' | 'text';
const KINDS: QuickKind[] = ['sticky', 'shape', 'text'];

/**
 * The menu at the free end of a connector released in the void (the editor fills the window, so its viewport
 * coordinates are client coordinates): picks the linked object to add there. On the ui `Menu` (P16-2): Escape, or a
 * press anywhere else, cancels (the connector disappears) once the menu has animated out (P18).
 */
export function QuickCreateMenu(props: QuickCreateMenuProps) {
  // Another connector released elsewhere moves the editor's menu to a new point: a new menu, whose state the old one's
  // pending `onCancel` (it would clear that state) must not touch.
  return <PointMenu key={`${props.at.x},${props.at.y}`} {...props} />;
}

type QuickCreateMenuProps = { at: Point; onPick: (k: QuickKind) => void; onCancel: () => void };

function PointMenu({ at, onPick, onCancel }: QuickCreateMenuProps) {
  const { t } = useTranslation();
  const anchor = useMemo(() => pointAnchor(at.x + 8, at.y + 8), [at.x, at.y]);
  const { open, request, complete } = useDeferredClose(onCancel);
  return (
    <Menu.Root
      open={open}
      modal={false}
      loopFocus
      onOpenChange={(next, details) => {
        // A pick closes the menu too: only the other ways cancel (the editor drops the menu itself on a pick).
        if (!next && details.reason !== 'item-press') request();
      }}
      onOpenChangeComplete={complete}
    >
      <Menu.Popup label={t('quickCreate.label')} anchor={anchor} sideOffset={0} finalFocus={false} focusFirst className="min-w-40">
        {KINDS.map((k) => (
          <Menu.Item key={k} onClick={() => onPick(k)}>
            {t(`tools.${k}`)}
          </Menu.Item>
        ))}
      </Menu.Popup>
    </Menu.Root>
  );
}
